import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import type { CreateLocationDto, LocationResponseDto, UpdateLocationDto } from '@peritasai/types';
import { locationApiClient } from '@peritasai/data-access';
import type { LocationEntity } from './location.entity';
import { locationRepository } from './repository';

export interface LocationsFilter {
  search?: string;
  page?: number;
  limit?: number;
  facilityId?: string;
}

export const locationKeys = {
  all: ['locations'] as const,
  lists: () => [...locationKeys.all, 'list'] as const,
  list: (filter: LocationsFilter) => [...locationKeys.lists(), filter] as const,
  searches: () => [...locationKeys.all, 'search'] as const,
  search: (query: string, page: number) => [...locationKeys.searches(), { query, page }] as const,
  detail: (id: string) => [...locationKeys.all, 'detail', id] as const,
} as const;

const LOCATION_LIST_LIMIT = 10;
const LOCATION_SEARCH_LIMIT = 10;

export function useLocations(
  filter: LocationsFilter = {},
  enabled = true
): UseQueryResult<{ data: LocationEntity[]; total: number; page: number; limit: number }> {
  return useQuery({
    queryKey: locationKeys.list(filter),
    queryFn: () =>
      locationRepository.getLocations({
        search: filter.search || undefined,
        page: filter.page ?? 1,
        limit: filter.limit ?? LOCATION_LIST_LIMIT,
        facilityId: filter.facilityId || undefined,
      }),
    enabled,
    staleTime: 60 * 1000,
  });
}

export function useLocation(
  id: string | null,
  options: { enabled?: boolean } = {}
): UseQueryResult<LocationEntity> {
  const { enabled = true } = options;
  return useQuery({
    queryKey: locationKeys.detail(id ?? ''),
    queryFn: () => locationRepository.getLocationById(id as string),
    enabled: enabled && !!id,
    staleTime: 5 * 60 * 1000,
  });
}

export function useAllLocations(
  filter: LocationsFilter = {},
  enabled = true
): UseQueryResult<LocationEntity[]> {
  return useQuery({
    queryKey: locationKeys.list(filter),
    queryFn: () =>
      locationRepository.getAllLocations({
        search: filter.search || undefined,
      }),
    enabled,
    staleTime: 60 * 1000,
  });
}

export async function ensureCachedLocations(
  query: UseQueryResult<LocationEntity[], Error>
): Promise<LocationEntity[]> {
  const { data, refetch } = query;
  const locationData = data ?? [];
  if (locationData?.length <= 0) {
    const result = await refetch();
    if (result.error) {
      throw result.error;
    }
    return result.data ?? [];
  }
  return locationData;
}

// Calls the API client directly (bypassing the repository) to return lightweight
// LocationResponseDto objects for search dropdowns — entity mapping is unnecessary here.
export function useLocationSearch(
  searchQuery: string | null,
  page = 1,
): UseQueryResult<{ data: LocationResponseDto[]; total: number; page: number; limit: number }> {
  return useQuery({
    queryKey: locationKeys.search(searchQuery ?? '', page),
    queryFn: () =>
      locationApiClient.getLocations({ search: searchQuery ?? undefined, limit: LOCATION_SEARCH_LIMIT, page }),
    enabled: searchQuery !== null,
    staleTime: 60 * 1000,
  });
}

export function useCreateLocation(): UseMutationResult<LocationEntity, Error, CreateLocationDto> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (dto: CreateLocationDto) => locationRepository.createLocation(dto),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: locationKeys.lists() });
    },
  });
}

export function useUpdateLocation(): UseMutationResult<
  LocationEntity,
  Error,
  { id: string; dto: UpdateLocationDto }
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, dto }: { id: string; dto: UpdateLocationDto }) =>
      locationRepository.updateLocation(id, dto),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: locationKeys.lists() });
    },
  });
}
